import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Table from '../../table/Table';
import Pagination from '../../pagination/Pagination';
import Button from '../../Button/Button';
import { Plus } from '../../Icons/Icons';
import {
  dataSourceVisits,
  dataSourceNotes,
  dataSourceContacts,
  dataSourceContactHistory,
  dataSourceSiteHistory,
  fetchHistories,
  fetchTabsData,
  getVisitsTable,
  notesTable,
  contactsTable,
  historyTable,
} from '../../../utils/siteDetailsTabs';
import {
  useCurrentUser, useToasts, useUnsavedChangesWarning, useCloseDialogWithUnsavedChanges,
} from '../../../hooks';
import { useUrlModals } from '../../../utils/useUrlModals';
import { useColumnMaxChars } from '../../../utils/columnTruncation';
import CommonModal from '../../Common/Modal/Modal';
import NewEditContactModal from '../../Contacts/NewEditContactModal';
import NoteViewAddEditModal from '../../Notes/Modals/NoteViewAddEditModal';
import CustomSelect from '../../FormInputs/CustomSelect/CustomSelect';
import { useFetchContacts } from '../../../utils/api/contact';
import { addExistingContactToSite } from '../../../utils/api/site';

const TABS = {
  CONTACTS: 'Contacts',
  VISITS: 'Visits',
  NOTES: 'Notes',
  CONTACT_HISTORY: 'Contact History',
  SITE_HISTORY: 'Site History',
};

const MODAL_CONFIG = {
  createContact: { isIdParam: false },
  noteModal: { isIdParam: true },
  editMode: { isIdParam: false },
};

const SiteDetailPageTabsContent = ({
  data, handleAddContact = () => {}, currentUserId, currentUserRole, handleChangeNote,
}) => {
  const [tabsData, setTabsData] = useState({
    data: [],
    title: '',
  });
  const { contactModelFormItems } = useCurrentUser();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { showToast } = useToasts();
  const [visitNameMeasureRef, visitNameMaxChars] = useColumnMaxChars();

  // URL-based modal management
  const { modals, openModal, closeModal } = useUrlModals(MODAL_CONFIG);

  // Access modals for note and edit mode
  const noteModal = modals.noteModal || { isOpen: false, value: null };
  const editModeModal = modals.editMode || { isOpen: false, value: null };

  // Functions to open/close edit mode
  const handleOpenEditMode = () => openModal('editMode', 'true');
  const handleCloseEditMode = () => closeModal('editMode');
  const handleCloseNoteModal = () => {
    closeModal('noteModal');
    closeModal('editMode');
  };

  // Contacts of all organization
  const [allContacts, setAllContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const fetchAllContacts = useFetchContacts({
    organizationId: data.organization_id,
    params: {
      name: '',
      contactType: '',
      company: '',
      relatedSites: '',
      priority: '',
      h2a: '',
      sortBy: '',
      direction: '',
      perPage: 1000,
      page: 1,
    },
    setContacts: setAllContacts,
    showToast,
  });

  useUnsavedChangesWarning(hasUnsavedChanges);

  const fetchPaginatedData = useCallback((page, resultCount) => {
    if (!data.data) {
      return;
    }
    const limit = 10;
    const APIversion = data.title === TABS.NOTES ? 'v1' : 'v3';
    const historyType = data.title === TABS.SITE_HISTORY ? 'site_history' : 'contact_history';
    if (data.length <= 5 && (data.title === TABS.SITE_HISTORY || data.title === TABS.CONTACT_HISTORY)) {
      setTabsData({
        data: data.data.paginated_histories,
        title: data.title,
      });
    } else if (data.length <= 10) {
      setTabsData({
        data: data.data,
        title: data.title,
      });
    }
    // pagination for history
    // we need data.length > 5 here because backend sends 5 history records at a time
    if (data.length > 5 && (data.title === TABS.SITE_HISTORY || data.title === TABS.CONTACT_HISTORY)) {
      fetchHistories(data.site_id, data.title, historyType, setTabsData, data.organization_id, page, limit, setTotalItems, showToast);
    } else if (data.length > 10) {
      fetchTabsData(APIversion, data.title, data.site_id, setTabsData, data.organization_id, page, limit, setTotalItems, resultCount, showToast);
    }
  }, [data.data, data.title, data.length, data.site_id, data.organization_id, showToast]);

  // fetch data when page changes of data length changes (when data is added/removed)
  useEffect(() => {
    const currentDataLength = data.length;
    // fetch for page 1 if tab title changed
    if (data.title !== tabsData.title) {
      fetchPaginatedData(1, currentDataLength);
      return;
    }
    // fetch current page if data length changes or current page changes
    fetchPaginatedData(currentPage, currentDataLength);
  }, [currentPage, data.length, data.title, tabsData.title, fetchPaginatedData]);

  // set the current page to 1 when the title changes
  useEffect(() => {
    setCurrentPage(1);
  }, [tabsData.title]);

  const onPageChange = (page) => {
    setCurrentPage(page);
  };

  // create data source for rows
  // data sending to table rows
  const dataSourceMap = {
    Visits: dataSourceVisits,
    Notes: dataSourceNotes,
    Contacts: dataSourceContacts,
    'Contact History': dataSourceContactHistory,
    'Site History': dataSourceSiteHistory,
  };
  const dataSource = () => {
    if (data.title !== tabsData.title) return [];
    if ((data.title === TABS.CONTACT_HISTORY || data.title === TABS.SITE_HISTORY) && data.length > 5 && tabsData.data.length === 5 && currentPage === 1) return [];
    try {
      return (dataSourceMap[tabsData.title] || dataSourceSiteHistory)(tabsData.data);
    } catch (error) {
      showToast({ message: error.message, type: 'error' });
      return [];
    }
  };

  const contactsList = allContacts.map(contact => ({ label: contact.name, value: contact.id }));

  const handleAddContactToSite = async () => {
    if (!selectedContact?.value) {
      showToast({ message: 'Please select a contact first', type: 'error' });
      return;
    }

    // Check if selectedContact exists in contactsList
    const existsInContactsList = contactsList.some(
      (contact) => contact.value === selectedContact.value,
    );
    if (!existsInContactsList) {
      showToast({ message: 'Please select a contact first', type: 'error' });
      return;
    }

    const alreadyAdded = tabsData.data.some(
      (contact) => contact.id === selectedContact.value,
    );

    if (alreadyAdded) {
      showToast({ message: 'The contact has already been added.', type: 'error' });
      return;
    }

    try {
      const result = await addExistingContactToSite(data.organization_id, data.site_id, selectedContact.value);
      const responseData = await result.json();
      if (!result.ok) {
        throw new Error(responseData.error || 'Failed to add contact.');
      }
      const addedContact = responseData.contact;
      setTabsData((prev) => ({
        ...prev,
        data: [...prev.data, addedContact],
        title: TABS.CONTACTS,
      }));
      handleAddContact(addedContact);

      showToast({ message: responseData.message || 'The contact has been added.', type: 'success' });
    } catch (error) {
      showToast({ message: error.message || 'Failed to add contact.', type: 'error' });
    } finally {
      setIsSearching(false);
      setSelectedContact(null);
    }
  };

  // data sending to table columns
  const tableColumns = () => {
    if (data.title === TABS.VISITS) {
      return getVisitsTable({
        measureRef: visitNameMeasureRef,
        maxChars: visitNameMaxChars,
        measuredRowId: dataSource()[0]?.id,
      });
    }
    if (data.title === TABS.NOTES) {
      return notesTable;
    }
    if (data.title === TABS.CONTACTS) {
      return contactsTable;
    }
    return historyTable;
  };

  const emptyState = () => {
    if (data.title === TABS.VISITS) {
      return 'Add your first visit to get started. ';
    }
    if (data.title === TABS.NOTES) {
      return 'Add your first note to get started. ';
    }
    if (data.title === TABS.CONTACTS) {
      return 'Add your first contact to get started. ';
    }
    if (data.title === TABS.CONTACT_HISTORY) {
      return 'Your contact history is empty.  You\'ll see a record of your interactions here.';
    }
    return 'Keep your site fresh. Make an update and it\'ll show up here.';
  };

  // if we are sending history data the rows in the table are not active
  const isHistory = () => {
    if (data.title === TABS.SITE_HISTORY || data.title === TABS.CONTACT_HISTORY) {
      return true;
    }
    return false;
  };

  const buttonLabel = () => {
    if (data.title === TABS.VISITS) {
      return 'Add visit';
    }
    if (data.title === TABS.NOTES) {
      return 'Add note';
    }
    return 'Add existing contact';
  };

  const handleCloseCreateContactModal = () => closeModal('createContact');

  const handleOpenNoteModal = (noteId) => {
    if (noteId) {
      openModal('noteModal', noteId);
    }
  };

  const handleCloseDialog = useCloseDialogWithUnsavedChanges({
    isUnsavedChanges: hasUnsavedChanges,
    setIsUnsavedChanges: setHasUnsavedChanges,
    redirectCallback: handleCloseCreateContactModal,
  });

  const closeDialogWithUnsavedChanges = () => {
    handleCloseDialog();
  };

  return (
    <>
      <div className={`mt-7 mb-6 min-h-100 md:min-h-125 ${(tabsData.data?.length > 0) ? 'gap-y-6' : 'flex flex-col pl-3 pt-3.75'}`}>
        {(tabsData.data?.length > 0)
          ? (
            <Table
              columns={tableColumns()}
              dataSource={dataSource()}
              typeOfData={data.title.toLowerCase().replace(/\s\(\d+\)$/, '')}
              isHistory={isHistory()}
              onClick={(noteId) => handleOpenNoteModal(noteId)}
            />
          )
          : (data.length === 0) && <div className="font-bold pb-6">{emptyState()}</div>}
        {(data.title === TABS.VISITS || data.title === TABS.NOTES || data.title === TABS.CONTACTS)
          && (
            <div className={`${tabsData.data?.length > 0 && 'flex mt-6'} space-y-3`}>
              <div className="space-y-3 w-full">
                {data.title === TABS.CONTACTS
                  && (
                    <Button
                      label="Create new contact"
                      size="md"
                      Icon={Plus}
                      className="font-medium gap-2 px-4.5"
                      onClick={() => {
                        setIsSearching(false);
                        setSelectedContact(null);
                        openModal('createContact');
                      }}
                    />
                  )}
                {((data.length === 0) || (dataSource().length !== 0)) && (
                  <>
                    {data.title === TABS.CONTACTS && isSearching && (
                    <div className="flex gap-2 items-center">
                      <CustomSelect
                        placeholder="search by name"
                        options={contactsList}
                        inputHeight={40}
                        value={selectedContact}
                        setValue={(val) => setSelectedContact(val)}
                        onInputChange
                        shouldFocus
                      />
                      <Button
                        label="Cancel"
                        color="secondary"
                        size="md"
                        onClick={() => {
                          setIsSearching(false);
                          setSelectedContact(null);
                        }}
                      />
                      <Button
                        label="Add"
                        size="md"
                        onClick={handleAddContactToSite}
                      />
                    </div>
                    )}

                    {data.title === TABS.CONTACTS && !isSearching && (
                    <Button
                      label={buttonLabel()}
                      size="md"
                      color="secondary"
                      Icon={Plus}
                      onClick={() => {
                        fetchAllContacts();
                        setIsSearching(true);
                      }}
                      className="font-medium gap-2.5"
                    />
                    )}

                    {(data.title === TABS.VISITS || data.title === TABS.NOTES) && (
                    <Button
                      label={buttonLabel()}
                      size="md"
                      color="primary"
                      Icon={Plus}
                      onClick={data.onClick}
                      className="font-medium gap-2.5"
                    />
                    )}
                  </>
                )}
              </div>
            </div>
          )}
      </div>
      {(data.length > 10) && (dataSource().length > 0) && (<Pagination itemsPerPage={10} totalItems={totalItems} currentPage={currentPage} setCurrentPage={onPageChange} marginTop="my-6" />)}
      {modals.createContact?.isOpen && (
        <CommonModal
          title="Create Contact"
          titlePosition="left"
          className="py-0"
          headerClassName="h-18.5 pl-5.5"
          titleStyle="text-2xl font-bold"
          boxClassName="w-1/2"
          handleClose={closeDialogWithUnsavedChanges}
          isHeaderDivider
          overflowY=""
          childrenHeight=""
        >
          <NewEditContactModal
            organizationId={data.organization_id}
            onCancel={closeDialogWithUnsavedChanges}
            initialModelFormItems={contactModelFormItems}
            setIsModalVisible={() => {}} // No longer needed since we're using URL modals
            showToast={showToast}
            associateToSiteID={data.site_id}
            setTabsData={setTabsData}
            handleAddContact={handleAddContact}
            setIsUnsavedChanges={setHasUnsavedChanges}
            closeModal={handleCloseCreateContactModal}
          />
        </CommonModal>
      )}

      {/* Note Modal */}
      {noteModal.isOpen && (
        <NoteViewAddEditModal
          activeNoteId={noteModal.value}
          organizationId={data.organization_id}
          setOpenAddEditModal={handleCloseNoteModal}
          showToast={showToast}
          isEditMode={editModeModal.isOpen}
          onOpenEditMode={handleOpenEditMode}
          onCloseEditMode={handleCloseEditMode}
          currentUserId={currentUserId}
          currentUserRole={currentUserRole}
          handleChangeNote={handleChangeNote}
        />
      )}
    </>
  );
};

SiteDetailPageTabsContent.propTypes = {
  data: PropTypes.object.isRequired,
  handleAddContact: PropTypes.func,
  currentUserId: PropTypes.number.isRequired,
  currentUserRole: PropTypes.string.isRequired,
  handleChangeNote: PropTypes.func.isRequired,
};

export default SiteDetailPageTabsContent;
